/**
 * "Bring a friend" (#3561): pure helpers shared by web and native
 * (native imports them as `@shared/lib/bringAFriend`).
 *
 * A member of a non-public club shares an event with the club's invite link
 * instead of the bare event link, because a non-member who opens a
 * members_only event hits a dead end (RSVP needs membership). The link is the
 * member's one reusable invite (POST /clubs/:clubId/invites/share-link) with
 * `?event=<short code>` so accepting lands on the event.
 */

/**
 * Event short codes as minted by backend/src/utils/shortCode.ts (#729):
 * 7 characters from `23456789abcdefghjkmnpqrstuvwxyz`. Every event producer
 * uses that generator. Anything else is not a short code.
 */
const EVENT_SHORT_CODE_PATTERN = /^[23456789abcdefghjkmnpqrstuvwxyz]{7}$/;

export function isEventShortCode(value: unknown): value is string {
  return typeof value === 'string' && EVENT_SHORT_CODE_PATTERN.test(value);
}

export interface BringAFriendViewer {
  visibility: 'public' | 'private' | 'members_only' | undefined;
  invitePermission: 'all_members' | 'leaders_only' | 'disabled' | undefined;
  role: 'owner' | 'leader' | 'moderator' | 'member' | 'none' | undefined;
}

/**
 * Client HINT for the "Bring a friend" action. The enforcement is
 * `checkInviteCreatePermission` in backend/src/routes/invites.ts; the two must
 * agree (Core Rule 48), so this mirrors it rule for rule:
 *
 * - not a member: no
 * - 'disabled': no, for everyone (leaders and owners included)
 * - 'leaders_only': leaders and owners only (role, not is_owner: the backend
 *   reads club_members.role)
 * - 'all_members': any member
 *
 * On top of the gate, public clubs keep the plain event Share (owner ruling on
 * #3561): their event link already works for a non-member. An unknown
 * permission or visibility (data still loading) fails closed to plain Share.
 */
export function canBringAFriend({ visibility, invitePermission, role }: BringAFriendViewer): boolean {
  if (visibility !== 'private' && visibility !== 'members_only') return false;
  if (!role || role === 'none') return false;
  if (invitePermission === 'all_members') return true;
  if (invitePermission === 'leaders_only') return role === 'leader' || role === 'owner';
  return false;
}

/**
 * The URL to share: the invite link plus the event short code. Built from the
 * server's `invite_url`; the event param is added only when it is a real short
 * code, so it can never smuggle anything into the link.
 */
export function buildBringAFriendUrl(inviteUrl: string, eventShortCode: string | null | undefined): string {
  if (!isEventShortCode(eventShortCode)) return inviteUrl;
  return `${inviteUrl}?event=${eventShortCode}`;
}

/**
 * Guard on the server's `invite_url` before it is shared (#3561 round 1,
 * George F4). It must be on the app's own origin, at exactly
 * `/invite/<code>` for the code the server returned, with no userinfo, query
 * or fragment. `appBaseUrl` is the client's own app origin
 * (`window.location.origin` on web, `APP_BASE_URL` on native).
 */
export function isTrustedInviteUrl(inviteUrl: unknown, code: unknown, appBaseUrl: string): boolean {
  if (typeof inviteUrl !== 'string' || typeof code !== 'string' || !/^[A-Za-z0-9]+$/.test(code)) {
    return false;
  }
  let url: URL;
  let base: URL;
  try {
    url = new URL(inviteUrl);
    base = new URL(appBaseUrl);
  } catch {
    return false;
  }
  return (
    url.origin === base.origin &&
    url.username === '' &&
    url.password === '' &&
    url.pathname === `/invite/${code}` &&
    url.search === '' &&
    url.hash === ''
  );
}

/** Collapse runs of whitespace (newlines, tabs) to one space and trim. */
export const collapseWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

/**
 * Share text. Event title and club name only: never the sharer's name (owner
 * ruling Q4 on #3561, and the "nobody named" guardrail). The last sentence is
 * the navigator's O1 ruling: say plainly that the link is open. Whitespace in
 * user-entered names is collapsed so a title cannot push the link off-screen.
 */
export function buildBringAFriendMessage(eventTitle: string, clubName: string): string {
  return `Come along to ${collapseWhitespace(eventTitle)} with ${collapseWhitespace(clubName)}. Anyone with this link can join.`;
}

/**
 * Where to go after accepting an invite. With a valid `event` short code, the
 * event page; otherwise the club, as before. The path is built here from a
 * validated short code, never taken from the URL, so a hostile `event` value
 * (`//evil.com`, `https://evil.com`) cannot redirect off-site (Rules 28/52).
 */
export function inviteLandingPath(clubSlug: string, eventParam: unknown): string {
  if (isEventShortCode(eventParam)) return `/event/${eventParam}`;
  return `/clubs/${clubSlug}`;
}
